/**
 * DashboardModals Component
 *
 * Orchestrates all modal components for the dashboard.
 * Uses App Bridge v4 route-based modals for better embedded app experience.
 *
 * REFACTORED: Uses DashboardModalContext instead of 60+ props.
 * @see app/contexts/DashboardModalContext.tsx
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.1
 */

import { useEffect, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { ModalVariant } from '../app-bridge/AppBridgeModal';
import { AppBridgeModal } from '../app-bridge/AppBridgeModal';
import { useModalRoute } from '../../hooks/core/useModalRoute';
import {
  useAddProductModal,
  useExportModal,
  useAlertDetailModal,
  useAlertActionModals,
  useProductSettingsModal,
  useTestProgressModal,
  useTestRemovalModal,
} from '../../contexts/DashboardModalContext';
import type { ModalMessage } from '~/hooks/core/useModalCommunication';
import { adminNavigation } from '~/utils/navigation';
import type { EnhancedAlert } from '~/types/dashboard';

/**
 * Container for all dashboard modals
 * Uses App Bridge route-based modals for AlertDetail, AddProduct, and ProductSettings
 * Now uses context instead of props for cleaner architecture
 */
export function DashboardModals() {
  const [searchParams] = useSearchParams();

  // Get modal state from context
  const addProduct = useAddProductModal();
  const exportModal = useExportModal();
  const alertDetail = useAlertDetailModal();
  const alertAction = useAlertActionModals();
  const productSettings = useProductSettingsModal();
  const testProgress = useTestProgressModal();
  const testRemoval = useTestRemovalModal();

  // Dynamic variant and title for Product Settings modal (changes between edit/confirm views)
  const [productSettingsVariant, setProductSettingsVariant] = useState<ModalVariant>('large');
  const [productSettingsTitle, setProductSettingsTitle] = useState<string>('Configure product alerts');

  // Route-based modals using useModalRoute hook
  const alertDetailRoute = useModalRoute('/app/modal/alert-detail');
  const addProductRoute = useModalRoute('/app/modal/add-product');
  const productSettingsRoute = useModalRoute('/app/modal/product-settings');
  const exportRoute = useModalRoute('/app/modal/export');
  const testProgressRoute = useModalRoute('/app/modal/test-data-progress');
  const testRemovalRoute = useModalRoute('/app/modal/test-data-removal');

  // Alert action modals (route-based for proper collapsing)
  const resolveAlertRoute = useModalRoute('/app/modal/resolve-alert');
  const dismissAlertRoute = useModalRoute('/app/modal/dismiss-alert');
  const notifyAlertRoute = useModalRoute('/app/modal/notify-alert');

  // Sync external open state with route-based modal for Alert Detail
  // NOTE: openKey is included as a dependency to force re-opening when the same alert
  // is clicked again (object reference wouldn't change, so we need this counter)
  useEffect(() => {
    if (alertDetail.isOpen && alertDetail.data) {
      alertDetailRoute.open({
        alertId: String(alertDetail.data.id),
        hasNotificationChannels: alertDetail.hasNotificationChannels ? '1' : '0',
        hasTestData: alertDetail.hasTestData ? '1' : '0',
      });
    } else if (!alertDetail.isOpen && alertDetailRoute.isOpen) {
      alertDetailRoute.close();
    }
  }, [alertDetail.isOpen, alertDetail.data, alertDetail.hasNotificationChannels, alertDetail.hasTestData, alertDetail.openKey]);

  // Sync external open state with route-based modal for Add Product
  useEffect(() => {
    if (addProduct.isOpen) {
      const params: Record<string, string> = {
        trackedVariantIds: addProduct.trackedVariantIds.join(','),
      };
      if (addProduct.preSelectedProductId) {
        params.preSelectedProductId = addProduct.preSelectedProductId;
      }
      addProductRoute.open(params);
    } else if (!addProduct.isOpen && addProductRoute.isOpen) {
      addProductRoute.close();
    }
  }, [addProduct.isOpen, addProduct.trackedVariantIds, addProduct.preSelectedProductId]);

  // Sync external open state with route-based modal for Product Settings
  useEffect(() => {
    if (productSettings.isOpen && productSettings.product) {
      productSettingsRoute.open({
        shopId: String(productSettings.shopId),
        productId: productSettings.product.product_id,
        productTitle: productSettings.product.product_title,
        productImage: productSettings.product.image_url || '',
        productSku: productSettings.product.product_sku || '',
        variantCount: String(productSettings.product.variant_count),
        totalInventory: String(productSettings.product.total_inventory),
        alertSeverities: JSON.stringify(productSettings.product.alert_severities || {}),
        hasTestData: productSettings.hasTestData ? '1' : '0',
      });
    } else if (!productSettings.isOpen && productSettingsRoute.isOpen) {
      productSettingsRoute.close();
    }
  }, [productSettings.isOpen, productSettings.product, productSettings.shopId, productSettings.hasTestData]);

  // Sync external open state with route-based modal for Export
  useEffect(() => {
    if (exportModal.isOpen) {
      const shopDomain = searchParams.get('shop') || '';
      exportRoute.open({
        shop: shopDomain,
        format: exportModal.format,
        fields: exportModal.fields.join(','),
        tab: exportModal.tabs[exportModal.selectedTab]?.content || '',
        itemCount: String(exportModal.selectedTab === 1 ? exportModal.productsCount : exportModal.alertsCount),
        isProductsTab: exportModal.selectedTab === 1 ? '1' : '0',
      });
    } else if (!exportModal.isOpen && exportRoute.isOpen) {
      exportRoute.close();
    }
  }, [exportModal.isOpen, exportModal.format, exportModal.fields, exportModal.selectedTab, exportModal.tabs, exportModal.productsCount, exportModal.alertsCount, searchParams]);

  // Sync external open state with route-based modal for Test Data Progress
  useEffect(() => {
    if (testProgress.isOpen) {
      const shopDomain = searchParams.get('shop') || '';
      testProgressRoute.open({
        shop: shopDomain,
      });
    } else if (!testProgress.isOpen && testProgressRoute.isOpen) {
      testProgressRoute.close();
    }
  }, [testProgress.isOpen, searchParams]);

  // Send progress updates to test progress modal via postMessage
  useEffect(() => {
    if (testProgress.isOpen && testProgress.progress) {
      // Find the modal iframe and send progress update
      const iframe = document.querySelector('ui-modal[src*="test-data-progress"] iframe') as HTMLIFrameElement;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'progress-update',
          payload: testProgress.progress,
        }, '*');
      }
    }
  }, [testProgress.isOpen, testProgress.progress]);

  // Sync external open state with route-based modal for Test Data Removal
  useEffect(() => {
    if (testRemoval.isOpen) {
      const shopDomain = searchParams.get('shop') || '';
      testRemovalRoute.open({
        shop: shopDomain,
        productCount: String(testRemoval.productCount),
        alertCount: String(testRemoval.alertCount),
        velocityCount: String(testRemoval.velocityCount),
      });
    } else if (!testRemoval.isOpen && testRemovalRoute.isOpen) {
      testRemovalRoute.close();
    }
  }, [testRemoval.isOpen, testRemoval.productCount, testRemoval.alertCount, testRemoval.velocityCount, searchParams]);

  // Sync alert action modals with alertModalState
  useEffect(() => {
    const shopDomain = searchParams.get('shop') || '';
    const selectedAlert = alertAction.state.selectedAlert;

    if (alertAction.state.showResolveModal && selectedAlert) {
      resolveAlertRoute.open({
        shop: shopDomain,
        alertId: String(selectedAlert.id),
        hasTestData: alertAction.hasTestData ? '1' : '0',
      });
    } else if (!alertAction.state.showResolveModal && resolveAlertRoute.isOpen) {
      resolveAlertRoute.close();
    }
  }, [alertAction.state.showResolveModal, alertAction.state.selectedAlert, alertAction.hasTestData, searchParams]);

  useEffect(() => {
    const shopDomain = searchParams.get('shop') || '';
    const selectedAlert = alertAction.state.selectedAlert;

    if (alertAction.state.showDismissModal && selectedAlert) {
      dismissAlertRoute.open({
        shop: shopDomain,
        alertId: String(selectedAlert.id),
        hasTestData: alertAction.hasTestData ? '1' : '0',
      });
    } else if (!alertAction.state.showDismissModal && dismissAlertRoute.isOpen) {
      dismissAlertRoute.close();
    }
  }, [alertAction.state.showDismissModal, alertAction.state.selectedAlert, alertAction.hasTestData, searchParams]);

  useEffect(() => {
    const shopDomain = searchParams.get('shop') || '';
    const selectedAlert = alertAction.state.selectedAlert;

    if (alertAction.state.showNotifyModal && selectedAlert) {
      notifyAlertRoute.open({
        shop: shopDomain,
        alertId: String(selectedAlert.id),
        hasTestData: alertAction.hasTestData ? '1' : '0',
      });
    } else if (!alertAction.state.showNotifyModal && notifyAlertRoute.isOpen) {
      notifyAlertRoute.close();
    }
  }, [alertAction.state.showNotifyModal, alertAction.state.selectedAlert, alertAction.hasTestData, searchParams]);

  // Handle messages from Alert Detail Modal
  const handleAlertDetailMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'alert-action') {
      const payload = message.payload as { action?: string; alertId?: number; alert?: EnhancedAlert } | undefined;
      const action = payload?.action;
      // Use alert from payload as fallback if context data is not yet synchronized
      const alertToAction = alertDetail.data || payload?.alert;
      if (!alertToAction) return;

      if (action === 'resolve') {
        // Close alert detail modal, then open standalone resolve modal after delay
        alertDetail.close();
        setTimeout(() => {
          alertAction.openResolve(alertToAction);
        }, 150);
      } else if (action === 'dismiss') {
        // Close alert detail modal, then open standalone dismiss modal after delay
        alertDetail.close();
        setTimeout(() => {
          alertAction.openDismiss(alertToAction);
        }, 150);
      }
    } else if (message.type === 'alert-notify') {
      // Handle notify button click from alert detail modal header
      // Use alert from payload as fallback if context data is not yet synchronized
      const payload = message.payload as { alertId?: number; alert?: EnhancedAlert } | undefined;
      const alertToNotify = alertDetail.data || payload?.alert;
      if (alertToNotify) {
        // Close the alert detail modal first, then open notify modal after a brief delay
        // to ensure the close animation completes and state is synchronized
        alertDetail.close();
        setTimeout(() => {
          alertAction.openNotify(alertToNotify);
        }, 150);
      }
    } else if (message.type === 'navigate-to-product') {
      const payload = message.payload as { productId?: string } | undefined;
      if (payload?.productId) {
        adminNavigation.product(payload.productId);
      }
    }
  }, [alertDetail, alertAction]);

  // Handle messages from Add Product Modal
  const handleAddProductMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'products-saved') {
      const payload = message.payload as { added?: string[]; removed?: string[] } | undefined;
      const added = payload?.added;
      const removed = payload?.removed;
      if (added || removed) {
        addProduct.onSave(added || [], removed || []);
      }
      addProduct.close();
    }
  }, [addProduct]);

  // Handle messages from Product Settings Modal
  const handleProductSettingsMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'product-settings-saved') {
      productSettings.onSaved();
      productSettings.close();
    } else if (message.type === 'request-variant-change') {
      // Handle dynamic variant and title change for edit/confirm view transitions
      const payload = message.payload as { variant?: ModalVariant; title?: string } | undefined;
      if (payload?.variant) {
        setProductSettingsVariant(payload.variant);
      }
      if (payload?.title !== undefined) {
        setProductSettingsTitle(payload.title);
      }
    } else if (message.type === 'navigate-to-product') {
      // Handle navigation to product in Shopify admin
      const payload = message.payload as { productId?: string } | undefined;
      if (payload?.productId) {
        adminNavigation.product(payload.productId);
      }
    }
  }, [productSettings]);

  // Handle messages from Export Modal
  const handleExportMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'export-started') {
      const payload = message.payload as { format?: 'csv' | 'xlsx' | 'json'; fields?: string[] } | undefined;
      if (payload?.format) {
        exportModal.setFormat(payload.format);
      }
      if (payload?.fields) {
        exportModal.setFields(payload.fields);
      }
      exportModal.onExport();
      exportModal.close();
    }
  }, [exportModal]);

  // Handle messages from Test Data Progress Modal
  const handleTestProgressMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'progress-closed') {
      testProgress.close();
    }
  }, [testProgress]);

  // Handle messages from Test Data Removal Modal
  const handleTestRemovalMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'removal-confirmed') {
      // Close the modal immediately, then perform deletion in background
      testRemovalRoute.close();
      testRemoval.close();
      testRemoval.onConfirm();
    }
  }, [testRemoval, testRemovalRoute]);

  // Handle messages from Resolve Alert Modal
  const handleResolveAlertMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'alert-resolved') {
      const payload = message.payload as { alertId?: number } | undefined;
      if (payload?.alertId) {
        alertAction.onResolved(payload.alertId);
      }
      alertAction.close();
    }
  }, [alertAction]);

  // Handle messages from Dismiss Alert Modal
  const handleDismissAlertMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'alert-dismissed') {
      const payload = message.payload as { alertId?: number } | undefined;
      if (payload?.alertId) {
        alertAction.onDismissed(payload.alertId);
      }
      alertAction.close();
    }
  }, [alertAction]);

  // Handle messages from Notify Alert Modal
  const handleNotifyAlertMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'alert-notified') {
      const payload = message.payload as { alertId?: number; channelIds?: number[] } | undefined;
      if (payload?.alertId && payload?.channelIds) {
        alertAction.onNotified(payload.alertId, payload.channelIds);
      }
      alertAction.close();
    }
  }, [alertAction]);

  // Close handlers that sync with context state
  const handleAlertDetailClose = useCallback(() => {
    alertDetailRoute.close();
    alertDetail.close();
  }, [alertDetailRoute, alertDetail]);

  const handleAddProductClose = useCallback(() => {
    addProductRoute.close();
    addProduct.close();
  }, [addProductRoute, addProduct]);

  const handleProductSettingsClose = useCallback(() => {
    productSettingsRoute.close();
    productSettings.close();
    // Reset variant and title to default when modal closes
    setProductSettingsVariant('large');
    setProductSettingsTitle('Configure product alerts');
  }, [productSettingsRoute, productSettings]);

  const handleExportClose = useCallback(() => {
    exportRoute.close();
    exportModal.close();
  }, [exportRoute, exportModal]);

  const handleTestProgressClose = useCallback(() => {
    testProgressRoute.close();
    testProgress.close();
  }, [testProgressRoute, testProgress]);

  const handleTestRemovalClose = useCallback(() => {
    testRemovalRoute.close();
    testRemoval.close();
  }, [testRemovalRoute, testRemoval]);

  // Close handlers for alert action modals
  const handleResolveAlertClose = useCallback(() => {
    resolveAlertRoute.close();
    alertAction.close();
  }, [resolveAlertRoute, alertAction]);

  const handleDismissAlertClose = useCallback(() => {
    dismissAlertRoute.close();
    alertAction.close();
  }, [dismissAlertRoute, alertAction]);

  const handleNotifyAlertClose = useCallback(() => {
    notifyAlertRoute.close();
    alertAction.close();
  }, [notifyAlertRoute, alertAction]);

  return (
    <>
      {/* Alert Detail Modal - App Bridge route-based */}
      <AppBridgeModal
        src={alertDetailRoute.src}
        open={alertDetailRoute.isOpen}
        onClose={handleAlertDetailClose}
        variant="large"
        onMessage={handleAlertDetailMessage}
      />

      {/* Manage Product Monitoring Modal - App Bridge route-based */}
      <AppBridgeModal
        src={addProductRoute.src}
        open={addProductRoute.isOpen}
        onClose={handleAddProductClose}
        variant="max"
        onMessage={handleAddProductMessage}
      />

      {/* Product Settings Modal - App Bridge route-based with dynamic variant and title */}
      <AppBridgeModal
        src={productSettingsRoute.src}
        open={productSettingsRoute.isOpen}
        onClose={handleProductSettingsClose}
        variant={productSettingsVariant}
        title={productSettingsTitle}
        onMessage={handleProductSettingsMessage}
      />

      {/* Export Modal - App Bridge route-based */}
      <AppBridgeModal
        src={exportRoute.src}
        open={exportRoute.isOpen}
        onClose={handleExportClose}
        variant="base"
        onMessage={handleExportMessage}
      />

      {/* Resolve Alert Modal - App Bridge route-based */}
      <AppBridgeModal
        src={resolveAlertRoute.src}
        open={resolveAlertRoute.isOpen}
        onClose={handleResolveAlertClose}
        variant="base"
        title="Mark alert as resolved"
        onMessage={handleResolveAlertMessage}
      />

      {/* Dismiss Alert Modal - App Bridge route-based */}
      <AppBridgeModal
        src={dismissAlertRoute.src}
        open={dismissAlertRoute.isOpen}
        onClose={handleDismissAlertClose}
        variant="base"
        title="Dismiss alert"
        onMessage={handleDismissAlertMessage}
      />

      {/* Notify Alert Modal - App Bridge route-based */}
      <AppBridgeModal
        src={notifyAlertRoute.src}
        open={notifyAlertRoute.isOpen}
        onClose={handleNotifyAlertClose}
        variant="base"
        title="Send notification"
        onMessage={handleNotifyAlertMessage}
      />

      {/* Test Data Progress Modal - App Bridge route-based */}
      <AppBridgeModal
        src={testProgressRoute.src}
        open={testProgressRoute.isOpen}
        onClose={handleTestProgressClose}
        variant="base"
        onMessage={handleTestProgressMessage}
      />

      {/* Test Data Removal Modal - App Bridge route-based */}
      <AppBridgeModal
        src={testRemovalRoute.src}
        open={testRemovalRoute.isOpen}
        onClose={handleTestRemovalClose}
        variant="base"
        title="Remove test data?"
        onMessage={handleTestRemovalMessage}
      />
    </>
  );
}

// =============================================================================
// Legacy Props Interface (for gradual migration)
// =============================================================================

/**
 * @deprecated Use DashboardModalProvider context instead
 * This interface is kept for backward compatibility during migration
 */
export interface DashboardModalsProps {
  // This interface is now empty - all props come from context
  // See DashboardModalProviderProps for the provider interface
}
